import { Navigate, Route } from 'react-router';

import { LOG_PRODUCTS, PRODUCTS } from '@/constants/products.constants';

import { ErrorFallback } from './components/ErrorBoundary/ErrorBoundary';
import { RequireAuth } from './components/RequiredAuth';
import { PERMISSIONS } from './constants/auth.constants';
import { PERMISSIONS_LOGIC } from './constants/common';
import { LayoutMain, LayoutPlain } from './layout';
import {
  Administration,
  AdministrationAISettings,
  AdministrationDownloads,
  AdministrationEnvironments,
  AdministrationGeneral,
  AdministrationIntegrations,
  AdministrationPcapRequests,
  AdministrationProducts,
  AdministrationRules,
  AdministrationTemplates,
  AlertRules,
  Announcements,
  Asset,
  Assets,
  Authenticate,
  Case,
  Cases,
  Dashboard,
  DashboardAlerts,
  DashboardInternal,
  DashboardMCSC,
  DashboardSummary,
  DashboardTickets,
  DashboardUsage,
  DashboardVMP,
  Devices,
  DraftAnnouncements,
  InternalTickets,
  LegacyRouteHandler,
  Login,
  Logout,
  NotFound,
  Organizations,
  PublishedAnnouncements,
  Reports,
  ReportsTemplate,
  TicketCreate,
  TicketDetails,
  Tickets,
  UserAccount,
  Users,
  VMPVulnerabilities,
  VulnerabilityDetails,
  VulnerabilityManagement
} from './pages';
import { AdministrationNotifications } from './pages/Administration';
import AllDevices from './pages/Administration/Devices/AllDevices/AllDevices';
import CloudSources from './pages/Administration/Devices/CloudSources/CloudSources';
import DevicesMain from './pages/Administration/Devices/DevicesMain';
import { AssetNotFound } from './pages/AssetNotFound';

export const Routes = () => {
  // Disable memoization for this component. Do not remove
  'use no memo';
  return (
    <>
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/authenticate" element={<Authenticate />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <LayoutMain />
          </RequireAuth>
        }
        errorElement={<ErrorFallback />}
      >
        <Route
          index
          element={
            <RequireAuth permissions={PERMISSIONS.TICKETS_READ} defaultRoute>
              <Navigate replace to="/dashboard" />
            </RequireAuth>
          }
        />
        <Route path="/dashboard" element={<Dashboard />}>
          <Route
            index
            element={
              <RequireAuth
                permissions={[PERMISSIONS.TICKETS_READ, PERMISSIONS.CASES_READ]}
              >
                <DashboardSummary />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/tickets"
            element={
              <RequireAuth permissions={PERMISSIONS.TICKETS_READ}>
                <DashboardTickets />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/mcsc"
            element={
              <RequireAuth permissions={[PERMISSIONS.TICKETS_READ]}>
                <DashboardMCSC />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/cases"
            element={
              <RequireAuth
                permissions={[PERMISSIONS.CASES_READ, PERMISSIONS.TICKETS_READ]}
              >
                <DashboardAlerts />
              </RequireAuth>
            }
          />
          {/* <Route
            path="/dashboard/events"
            element={<span>Dashboard events</span>}
          /> */}
          <Route
            path="/dashboard/usage"
            element={
              <RequireAuth
                requiredProducts={{
                  products: LOG_PRODUCTS,
                  logic: PERMISSIONS_LOGIC.OR
                }}
                permissions={PERMISSIONS.TICKETS_READ}
              >
                <DashboardUsage />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/vmp"
            element={
              <RequireAuth
                permissions={[PERMISSIONS.VMP_READ, PERMISSIONS.TICKETS_READ]}
                requiredProducts={{ products: [PRODUCTS.VMPE] }}
              >
                <DashboardVMP />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/internal"
            element={
              <RequireAuth
                permissions={[PERMISSIONS.INTERNAL, PERMISSIONS.TICKETS_READ]}
              >
                <DashboardInternal />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path="/asset-inventory"
          element={
            <RequireAuth permissions={[PERMISSIONS.ASSET_READ]}>
              <Assets />
            </RequireAuth>
          }
        />
        <Route
          path="/tickets"
          element={
            <RequireAuth permissions={PERMISSIONS.TICKETS_READ}>
              <Tickets />
            </RequireAuth>
          }
        />
        <Route
          path="/cases"
          element={
            <RequireAuth permissions={[PERMISSIONS.CASES_READ]}>
              <Cases />
            </RequireAuth>
          }
        />
        <Route
          path="/cases-internal"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.CASES_READ, PERMISSIONS.INTERNAL]}
            >
              <Cases />
            </RequireAuth>
          }
        />
        <Route
          path="/alert-rules"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.CASES_READ, PERMISSIONS.INTERNAL]}
            >
              <AlertRules />
            </RequireAuth>
          }
        />
        <Route
          path="/ticket/:id"
          element={
            <RequireAuth permissions={PERMISSIONS.TICKETS_READ}>
              <TicketDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/ticket/edit/:id"
          element={
            <RequireAuth permissions={PERMISSIONS.TICKETS_UPDATE}>
              <TicketCreate />
            </RequireAuth>
          }
        />
        <Route
          path="/ticket/create"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.TICKETS_CREATE, PERMISSIONS.INTERNAL]}
            >
              <TicketCreate />
            </RequireAuth>
          }
        />

        <Route
          path="/devices"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.DEVICES_READ, PERMISSIONS.INTERNAL]}
            >
              <DevicesMain />
            </RequireAuth>
          }
        >
          <Route
            index
            element={
              <RequireAuth
                permissions={[PERMISSIONS.DEVICES_READ, PERMISSIONS.INTERNAL]}
              >
                <AllDevices />
              </RequireAuth>
            }
          />
          <Route
            path="/devices/cloud-sources"
            element={
              <RequireAuth
                permissions={[PERMISSIONS.DEVICES_READ, PERMISSIONS.INTERNAL]}
              >
                <CloudSources isCustomerCloudSource={false} />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path="/reports"
          element={
            <RequireAuth permissions={PERMISSIONS.REPORT_READ}>
              <Reports />
            </RequireAuth>
          }
        />
        <Route
          path="/announcements"
          element={
            <RequireAuth permissions={PERMISSIONS.ANNOUNCEMENT_READ}>
              <Announcements />
            </RequireAuth>
          }
        >
          <Route index element={<PublishedAnnouncements />} />
          <Route
            path="/announcements/drafts"
            element={
              <RequireAuth permissions={PERMISSIONS.ANNOUNCEMENT_CREATE}>
                <DraftAnnouncements />
              </RequireAuth>
            }
          />
        </Route>
        <Route path="/user-account" element={<UserAccount />} />
        <Route
          path="/organizations"
          element={
            <RequireAuth permissions={PERMISSIONS.ALL}>
              <Organizations />
            </RequireAuth>
          }
        />
        <Route
          path="/administration"
          element={
            <RequireAuth permissions={PERMISSIONS.ADMIN_READ}>
              <Administration />
            </RequireAuth>
          }
        >
          <Route index element={<AdministrationGeneral />} />
          <Route
            path="/administration/notifications"
            element={<AdministrationNotifications />}
          />
          <Route
            path="/administration/devices"
            element={
              <RequireAuth permissions={[PERMISSIONS.DEVICES_READ]}>
                <Devices />
              </RequireAuth>
            }
          />
          <Route
            path="/administration/cloud-sources"
            element={
              <RequireAuth
                permissions={{ permissions: [PERMISSIONS.SOURCES_READ] }}
              >
                <CloudSources isCustomerCloudSource />
              </RequireAuth>
            }
          />
          <Route
            path="/administration/downloads"
            element={<AdministrationDownloads />}
          />
          <Route
            path="/administration/products"
            element={<AdministrationProducts />}
          />
          <Route
            path="/administration/templates"
            element={
              <RequireAuth
                permissions={[
                  PERMISSIONS.ADMIN_READ,
                  PERMISSIONS.INTERNAL,
                  PERMISSIONS.TICKETS_UPDATE
                ]}
              >
                <AdministrationTemplates />
              </RequireAuth>
            }
          />
          <Route
            path="/administration/environments"
            element={<AdministrationEnvironments />}
          />
          <Route
            path="/administration/rules"
            element={
              <RequireAuth permissions={PERMISSIONS.SENSOR_RULES_READ}>
                <AdministrationRules />
              </RequireAuth>
            }
          />
          <Route
            path="/administration/pcaps"
            element={<AdministrationPcapRequests />}
          />
          <Route
            path="/administration/integrations"
            element={
              <RequireAuth permissions={PERMISSIONS.ADMIN_READ}>
                <AdministrationIntegrations />
              </RequireAuth>
            }
          />
          <Route
            path="/administration/ai-settings"
            element={<AdministrationAISettings />}
          />
          {/* Customer facing users page */}
          <Route path="/administration/users" element={<Users />} />
        </Route>
        {/* Route handling for vulnerability management tables pages handled in VulnerabilityManagement.tsx, can be moved here in later releases */}
        <Route
          path="/vulnerability-management"
          element={
            <RequireAuth
              permissions={PERMISSIONS.VMP_READ}
              requiredProducts={{ products: [PRODUCTS.VMPE] }}
            >
              <VulnerabilityManagement />
            </RequireAuth>
          }
        >
          {/* redirect to vulnerabilities tab when /vulnerability-management is accessed (assets tab used to be at the index route) */}
          <Route
            index
            element={
              <Navigate
                to="/vulnerability-management/vulnerabilities"
                replace
              />
            }
          />
          <Route
            path="/vulnerability-management/vulnerabilities"
            element={
              <RequireAuth
                permissions={PERMISSIONS.VMP_READ}
                requiredProducts={{ products: [PRODUCTS.VMPE] }}
              >
                <VMPVulnerabilities />
              </RequireAuth>
            }
          />
        </Route>
        <Route
          path="/vulnerability-management/vulnerability-details/:id"
          element={
            <RequireAuth
              permissions={PERMISSIONS.VMP_READ}
              requiredProducts={{ products: [PRODUCTS.VMPE] }}
            >
              <VulnerabilityDetails />
            </RequireAuth>
          }
        />
        <Route
          path="/asset/:id"
          element={
            <RequireAuth permissions={[PERMISSIONS.ASSET_READ]}>
              <Asset />
            </RequireAuth>
          }
        />
        <Route
          path="/case/:id"
          element={
            <RequireAuth permissions={[PERMISSIONS.CASES_READ]}>
              <Case />
            </RequireAuth>
          }
        />
        <Route
          path="/case-internal/:id"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.CASES_READ, PERMISSIONS.INTERNAL]}
            >
              <Case />
            </RequireAuth>
          }
        />
        {/* Legacy Routes */}
        <Route
          path="/organizations/:org/conversations/:id"
          element={<LegacyRouteHandler routeType="conversation" />}
        />
        <Route
          path="/organizations/:org/reports"
          element={<Navigate to="/reports" />}
        />
        <Route
          path="/organizations/ticket/:id"
          element={<LegacyRouteHandler routeType="ticket" />}
        />

        <Route path="/not-found" element={<NotFound />} />
        <Route path="/asset-not-found" element={<AssetNotFound />} />
        <Route path="*" element={<Navigate to="/" />} />

        <Route
          path="/internal-tickets"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.TICKETS_READ, PERMISSIONS.INTERNAL]}
            >
              <InternalTickets />
            </RequireAuth>
          }
        />
      </Route>
      <Route
        path="/"
        element={
          <RequireAuth>
            <LayoutPlain />
          </RequireAuth>
        }
      >
        <Route
          path="/mdr-reports-template"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.REPORT_READ, PERMISSIONS.INTERNAL]}
            >
              <ReportsTemplate />
            </RequireAuth>
          }
        />
      </Route>
      <Route
        path="/"
        element={
          <RequireAuth>
            <LayoutPlain />
          </RequireAuth>
        }
      >
        <Route
          path="/mdr-reports-template"
          element={
            <RequireAuth
              permissions={[PERMISSIONS.REPORT_READ, PERMISSIONS.INTERNAL]}
            >
              <ReportsTemplate />
            </RequireAuth>
          }
        />
      </Route>
    </>
  );
};

export default Routes;
